import { baseApi } from "./baseApi";
import {
  ImportExportJobsResponse,
  ImportExportJobsParams,
  ImportExportStatsResponse,
} from "../../pages/Settings/components/ImportsExports/utils/types";
//import { mockSettingsDataByBillingConfig, mockSettingsDataByDashboardConfig, mockSettingsDataByEscalationMatrix, mockSettingsDataByTicketConfig, mockSettingsDataByUserEmailNotifications, mockSettingsDataByVerbaChat, mockSettingsDataByVerticals, mockSettingsDataByWhatsappConfig, mockSettingsTabsResponse } from "../../utils/constants/mockedSettingPageResponse";

// Settings tab types
export type SettingsTabName =
  | "dashboard_config"
  | "global_settings"
  | "widget"
  | "email"
  | "call"
  | "whatsapp"
  | "sms"
  | "user_email_notifications"
  | "workflo_config"
  | "verticals"
  | "billing_config"
  | "ticket_config"
  | "escalation_matrix"
  | "activity"
  | "lead_form"
  | "communication_sidebar"
  | "imports_exports"
  | "lead_qualification_rules";

export interface SettingsTab {
  display_name: string;
  value: SettingsTabName;
  metadata?: {
    modality_enabled?: boolean;
    secondary_tab?: boolean;
  };
}

export interface GetSettingsTabsResponse {
  tabs: SettingsTab[];
}

// Workflo types
interface CreatedBy {
  user_id: string | null;
  user_name: string | null;
}

export interface WorkfloCategory {
  _id: string;
  logo_url: string | null;
  description: string | null;
  name: string;
  created_by: CreatedBy;
  client_code: string | null;
  org_code: string | null;
  created_at: string;
  updated_at: string;
}

export interface GetWorkfloCategoriesResponse {
  total: number;
  page: number;
  page_size: number;
  items: WorkfloCategory[];
}

export interface WorkfloUseCase {
  _id: string;
  name: string;
  category_id: string;
  client_code: string | null;
  org_code: string | null;
  created_by: CreatedBy;
  created_at: string;
  updated_at: string;
}

export interface GetWorkfloUseCasesResponse {
  total: number;
  page: number;
  page_size: number;
  items: WorkfloUseCase[];
}

export interface WorkfloBranch {
  _id: string;
  name: string;
  category_id: string;
  use_case_id: string;
  client_code: string | null;
  org_code: string | null;
  created_by: CreatedBy;
  created_at: string;
  updated_at: string;
}

export interface GetWorkfloBranchesResponse {
  total: number;
  page: number;
  page_size: number;
  items: WorkfloBranch[];
}

export interface WorkfloOutcome {
  _id: string;
  name: string;
  description: string;
  created_at: string;
  updated_at: string;
  mapped: boolean;
}

export interface GetWorkfloOutcomesResponse {
  total: number;
  page: number;
  page_size: number;
  items: WorkfloOutcome[];
}

export interface GetWorkfloOutcomesParams {
  categoryId: string;
  useCaseId: string;
  branchId?: string | null;
}

// Activity logs types
export interface ActivityLogItem {
  config_diff: {
    old: Record<string, unknown>;
    new: Record<string, unknown>;
  };
  remarks: string;
  client_code: string;
  user_id: string;
  user_name: string;
  updated_at: string;
  tab_name: string | null;
}

export interface GetActivityLogsResponse {
  settings: {
    items: ActivityLogItem[];
    total: number;
    page: number;
    page_size: number;
    total_pages: number;
    has_next: boolean;
    has_previous: boolean;
  };
}

export interface GetActivityLogsParams {
  page: number;
  page_size: number;
  search?: string;
}

export interface SearchActivityLogsParams {
  search_query: string;
  page: number;
  page_size: number;
}

// Search API returns items directly (not nested under 'settings')
export interface SearchActivityLogsResponse {
  items: ActivityLogItem[];
  total: number;
  page: number;
  page_size: number;
  total_pages: number;
  has_next: boolean;
  has_previous: boolean;
}

// Lead form settings API types
export interface LeadFormFieldApi {
  field_key: string;
  display_name: string;
  description?: string;
  placeholder?: string | null;
  is_mandatory_by_default?: boolean;
  field_data_type: string;
  input_type: string;
  allows_multiple: boolean;
  option_source: string | null;
  is_enabled: boolean;
  is_mandatory: boolean;
  display_order: number;
  has_dynamic_values: boolean;
  possible_values: string[] | null;
  parent_field_key: string | null;
  child_field_keys: string[] | null;
}

export interface GetLeadFormSettingsResponse {
  settings: {
    fields: LeadFormFieldApi[];
  };
}

export interface CommunicationSidebarSubsectionApi {
  is_enabled: boolean;
  display_order: number;
  is_mandatory: boolean;
}

export interface CommunicationSidebarSectionApi {
  section: string;
  name?: string;
  description?: string;
  enabled: boolean;
  subsections: Record<string, CommunicationSidebarSubsectionApi> | null;
}

export interface GetCommunicationSidebarSettingsResponse {
  settings: {
    sections: CommunicationSidebarSectionApi[];
  };
}

export const settingsApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    //GET CALLS----------------------------------

    // Fetches all tabs info
    getSettingsTabs: builder.query<GetSettingsTabsResponse, void>({
      // queryFn: () => ({ data: mockSettingsTabsResponse }),
      query: () => ({
        url: "/settings/tabs",
      }),
      providesTags: [{ type: "Settings", id: "GetSettingsTabs" }],
    }),

    // Get workflo categories
    getWorkfloCategories: builder.query<
      GetWorkfloCategoriesResponse,
      { page: number; page_size: number }
    >({
      query: ({ page, page_size }) => ({
        url: "/settings/workflo/categories",
        params: { page, page_size },
      }),
      providesTags: [{ type: "Settings", id: "GetWorkfloCategories" }],
    }),

    // Get workflo use cases by category
    getWorkfloUseCases: builder.query<
      GetWorkfloUseCasesResponse,
      { categoryId: string; page: number; page_size: number }
    >({
      query: ({ categoryId, page, page_size }) => ({
        url: `/settings/workflo/${categoryId}/use_cases`,
        params: { page, page_size },
      }),
      providesTags: [{ type: "Settings", id: "GetWorkfloUseCases" }],
    }),

    // Get workflo branches by category and use case
    getWorkfloBranches: builder.query<
      GetWorkfloBranchesResponse,
      { categoryId: string; useCaseId: string; page: number; page_size: number }
    >({
      query: ({ categoryId, useCaseId, page, page_size }) => ({
        url: `/settings/workflo/${categoryId}/${useCaseId}/branches`,
        params: { page, page_size },
      }),
      providesTags: [{ type: "Settings", id: "GetWorkfloBranches" }],
    }),

    // Get workflo outcomes
    getWorkfloOutcomes: builder.query<
      GetWorkfloOutcomesResponse,
      GetWorkfloOutcomesParams
    >({
      query: ({ categoryId, useCaseId, branchId }) => ({
        url: `/settings/workflo/outcomes/${categoryId}/${useCaseId}`,
        params: branchId ? { branch_id: branchId } : {},
      }),
      providesTags: [{ type: "Settings", id: "GetWorkfloOutcomes" }],
    }),

    // Get settings activity logs
    getSettingsActivityLogs: builder.query<
      GetActivityLogsResponse,
      GetActivityLogsParams
    >({
      query: ({ page, page_size, search }) => ({
        url: "/settings/activity",
        params: { page, page_size, ...(search && { search }) },
      }),
      providesTags: [{ type: "Settings", id: "GetSettingsActivityLogs" }],
    }),

    // Search settings activity logs
    searchSettingsActivityLogs: builder.query<
      SearchActivityLogsResponse,
      SearchActivityLogsParams
    >({
      query: (params) => ({
        url: "/settings/search-activity",
        method: "POST",
        body: {
          search_query: params.search_query,
          page: params.page,
          page_size: params.page_size,
        },
      }),
      providesTags: [{ type: "Settings", id: "SearchSettingsActivityLogs" }],
    }),

    // Get settings by tab name
    getSettingsDataByTab: builder.query({
      //     queryFn: (tabName: SettingsTabName) => {
      //         if (tabName === "ticket_config") {
      //         return { data: mockSettingsDataByTicketConfig };
      //     } else if (tabName === "billing_config") {
      //         return { data: mockSettingsDataByBillingConfig };
      //     } else if (tabName === "verticals") {
      //         return { data: mockSettingsDataByVerticals };
      //         } else if (tabName === "user_email_notifications") {
      //             return { data: mockSettingsDataByUserEmailNotifications };
      //         }
      //         else if (tabName === "escalation_matrix") {
      //             return { data: mockSettingsDataByEscalationMatrix };
      //         }
      //         else if (tabName === "whatsapp") {
      //             return { data: mockSettingsDataByWhatsappConfig };
      //         }
      //         else if (tabName === "widget") {
      //             return { data: mockSettingsDataByVerbaChat };
      //         }
      //         else if (tabName === "dashboard_config") {
      //             return { data: mockSettingsDataByDashboardConfig };
      //         }
      //         else{
      //         return { data: null };
      //     }
      // },
      query: (tabName: SettingsTabName) => ({
        url: `/settings/${tabName}`,
      }),
      providesTags: [{ type: "Settings", id: "GetSettingsDataByTab" }],
    }),

    // Get settings by tab name with bot version (for widget)
    getSettingsDataByTabWithVersion: builder.query<
      unknown,
      { tabName: SettingsTabName; bot_version: string }
    >({
      query: ({ tabName, bot_version }) => ({
        url: `/settings/${tabName}`,
        params: { bot_version },
      }),
      providesTags: [
        { type: "Settings", id: "GetSettingsDataByTabWithVersion" },
      ],
      keepUnusedDataFor: 0,
    }),

    getLeadFormSettings: builder.query<GetLeadFormSettingsResponse, void>({
      query: () => ({
        url: "/settings/lead_form",
      }),
      providesTags: [{ type: "Settings", id: "GetLeadFormSettings" }],
    }),

    getCommunicationSidebarSettings: builder.query<
      GetCommunicationSidebarSettingsResponse,
      void
    >({
      query: () => ({
        url: "/settings/communication_sidebar",
      }),
      providesTags: [
        { type: "Settings", id: "GetCommunicationSidebarSettings" },
      ],
    }),

    getImportExportJobs: builder.query<
      ImportExportJobsResponse,
      ImportExportJobsParams
    >({
      query: (params) => ({
        url: "/settings/imports-exports/jobs",
        params: Object.fromEntries(
          Object.entries(params).filter(([, v]) => v !== undefined && v !== "")
        ),
      }),
      providesTags: [{ type: "Settings", id: "GetImportExportJobs" }],
    }),

    getImportExportStats: builder.query<ImportExportStatsResponse, void>({
      query: () => ({
        url: "/settings/imports-exports/jobs/stats",
      }),
      providesTags: [{ type: "Settings", id: "GetImportExportStats" }],
    }),

    retryImportExportJob: builder.mutation<void, string>({
      query: (jobId) => ({
        url: `/data-io/jobs/${jobId}/retry`,
        method: "POST",
      }),
      invalidatesTags: [
        { type: "Settings", id: "GetImportExportJobs" },
        { type: "Settings", id: "GetImportExportStats" },
      ],
    }),

    downloadImportExportFile: builder.mutation<
      { download_url: string; expires_in_seconds?: number },
      string
    >({
      query: (jobId) => ({
        url: `/data-io/jobs/${jobId}/download`,
      }),
    }),

    createDataExport: builder.mutation<
      { job_id?: string } & Record<string, unknown>,
      {
        entity_type: string;
        file_format: string;
        file_name_hint?: string;
        view_id?: string;
        entity_filter?: Record<string, unknown>;
      }
    >({
      query: (body) => ({
        url: "/data-io/exports",
        method: "POST",
        body,
      }),
      invalidatesTags: [
        { type: "Settings", id: "GetImportExportJobs" },
        { type: "Settings", id: "GetImportExportStats" },
      ],
    }),

    //POST CALLS----------------------------------

    // Add workflo category
    addWorkfloCategory: builder.mutation<
      WorkfloCategory,
      { name: string; description: string }
    >({
      query: (payload) => ({
        url: "/settings/workflo/categories",
        method: "POST",
        body: payload,
      }),
      invalidatesTags: [{ type: "Settings", id: "GetWorkfloCategories" }],
    }),

    // Edit workflo category
    editWorkfloCategory: builder.mutation<
      WorkfloCategory,
      { categoryId: string; name: string; description: string }
    >({
      query: ({ categoryId, ...payload }) => ({
        url: `/settings/workflo/categories/${categoryId}`,
        method: "PUT",
        body: payload,
      }),
      invalidatesTags: [{ type: "Settings", id: "GetWorkfloCategories" }],
    }),

    // Add workflo use case
    addWorkfloUseCase: builder.mutation<
      WorkfloUseCase,
      { category_id: string; name: string }
    >({
      query: (payload) => ({
        url: "/settings/workflo/use_cases",
        method: "POST",
        body: payload,
      }),
      invalidatesTags: [{ type: "Settings", id: "GetWorkfloUseCases" }],
    }),

    // Edit workflo use case
    editWorkfloUseCase: builder.mutation<
      WorkfloUseCase,
      { useCaseId: string; name: string }
    >({
      query: ({ useCaseId, ...payload }) => ({
        url: `/settings/workflo/use_cases/${useCaseId}`,
        method: "PUT",
        body: payload,
      }),
      invalidatesTags: [{ type: "Settings", id: "GetWorkfloUseCases" }],
    }),

    // Add workflo branch
    addWorkfloBranch: builder.mutation<
      WorkfloBranch,
      { category_id: string; use_case_id: string; name: string }
    >({
      query: (payload) => ({
        url: "/settings/workflo/branches",
        method: "POST",
        body: payload,
      }),
      invalidatesTags: [{ type: "Settings", id: "GetWorkfloBranches" }],
    }),

    // Edit workflo branch
    editWorkfloBranch: builder.mutation<
      WorkfloBranch,
      { branchId: string; name: string }
    >({
      query: ({ branchId, ...payload }) => ({
        url: `/settings/workflo/branches/${branchId}`,
        method: "PUT",
        body: payload,
      }),
      invalidatesTags: [{ type: "Settings", id: "GetWorkfloBranches" }],
    }),

    // Save workflo outcome mappings
    saveOutcomeMappings: builder.mutation<
      void,
      {
        category_id: string;
        use_case_id: string;
        extraction_ids: string[];
        branch_id?: string;
      }
    >({
      query: ({ branch_id, ...rest }) => ({
        url: "/settings/workflo/extract-mappings",
        method: "POST",
        body: branch_id ? { ...rest, branch_id } : rest,
      }),
      invalidatesTags: [{ type: "Settings", id: "GetWorkfloOutcomes" }],
    }),

    // Setup new client
    setupClient: builder.mutation<
      void,
      {
        new_client_code: string;
        new_client_name: string;
        old_client_code?: string;
        user_ids?: string[];
      }
    >({
      query: (payload) => ({
        url: "/settings/setup-client",
        method: "POST",
        body: payload,
      }),
    }),

    //PATCH CALLS----------------------------------
    patchSettingsDataByTab: builder.mutation({
      query: (payload: {
        tabName: SettingsTabName;
        data: Record<string, unknown>;
        params?: Record<string, string>;
      }) => ({
        url: `/settings/${payload.tabName}`,
        method: "PATCH",
        body: payload.data,
        params: payload.params,
      }),
      invalidatesTags: (_result, _error, payload) => {
        const tags = [
          { type: "Settings" as const, id: "GetSettingsDataByTab" },
          { type: "Settings" as const, id: "GetSettingsTabs" },
          { type: "Settings" as const, id: "GetSettingsDataByTabWithVersion" },
        ];
        if (payload.tabName === "communication_sidebar") {
          tags.push({
            type: "Settings" as const,
            id: "GetCommunicationSidebarSettings",
          });
        }
        if (payload.tabName === "lead_form") {
          tags.push({ type: "Settings" as const, id: "GetLeadFormSettings" });
        }
        return tags;
      },
    }),
  }),
});

export const {
  useGetSettingsTabsQuery,
  useGetSettingsDataByTabQuery,
  useGetSettingsDataByTabWithVersionQuery,
  useGetLeadFormSettingsQuery,
  useGetCommunicationSidebarSettingsQuery,
  useGetSettingsActivityLogsQuery,
  useSearchSettingsActivityLogsQuery,
  useGetWorkfloCategoriesQuery,
  useGetWorkfloUseCasesQuery,
  useGetWorkfloBranchesQuery,
  useGetWorkfloOutcomesQuery,
  useGetImportExportJobsQuery,
  useGetImportExportStatsQuery,
  useRetryImportExportJobMutation,
  useDownloadImportExportFileMutation,
  useCreateDataExportMutation,
  useAddWorkfloCategoryMutation,
  useEditWorkfloCategoryMutation,
  useAddWorkfloUseCaseMutation,
  useEditWorkfloUseCaseMutation,
  useAddWorkfloBranchMutation,
  useEditWorkfloBranchMutation,
  useSaveOutcomeMappingsMutation,
  useSetupClientMutation,
  usePatchSettingsDataByTabMutation,
} = settingsApi;
